import React, { useCallback, useEffect, useState } from 'react';
import { usePolling } from '@/hooks/usePolling';
import { Button, Card, Progress, Spin, Typography } from 'antd';
import { ExperimentOutlined, ReloadOutlined } from '@ant-design/icons';
import { bidAnalysisApi, BidAnalysisDto } from '@/api/bidAnalysisApi';
import { analysisApi, ProposalAnalysisDto } from '@/api/analysisApi';

const { Text } = Typography;

interface AIScoreCardProps {
  opportunityId: string;
  type: 'bid' | 'proposal';
}

type AnalysisStatus = 'PENDING' | 'RUNNING' | 'COMPLETED' | 'FAILED';

const POLL_INTERVAL = 3000;
const SCORE_THRESHOLD = 80;

const getScoreColor = (score: number): string => {
  if (score >= 80) return '#52c41a';
  if (score >= 60) return '#faad14';
  return '#ff4d4f';
};

const AIScoreCard: React.FC<AIScoreCardProps> = ({ opportunityId, type }) => {
  const [score, setScore] = useState<number | null>(null);
  const [status, setStatus] = useState<AnalysisStatus | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [triggering, setTriggering] = useState(false);
  const fetchLatest = useCallback(async (): Promise<{ status: AnalysisStatus; score?: number; error?: string } | null> => {
    try {
      if (type === 'bid') {
        const res = await bidAnalysisApi.getLatest(opportunityId);
        if (res.status === 204) return null;
        const data = res.data as BidAnalysisDto;
        return {
          status: data.status,
          score: data.overallScore != null ? Math.round(data.overallScore) : undefined,
          error: data.errorMessage,
        };
      } else {
        const res = await analysisApi.getLatest(opportunityId);
        if (res.status === 204) return null;
        const data = res.data as ProposalAnalysisDto;
        return {
          status: data.status,
          score: data.overallScore != null ? Math.round(data.overallScore) : undefined,
          error: data.errorMessage,
        };
      }
    } catch {
      return null;
    }
  }, [opportunityId, type]);

  const applyResult = useCallback(
    (r: { status: AnalysisStatus; score?: number; error?: string } | null) => {
      if (!r) {
        setStatus(null);
        setScore(null);
        return;
      }
      setStatus(r.status);
      setScore(r.score ?? null);
      setErrorMessage(r.error ?? null);
    },
    [],
  );

  const isRunning = status === 'PENDING' || status === 'RUNNING';

  // Poll while running; pauses when the tab is hidden, stops on terminal.
  usePolling(
    async () => {
      const data = await fetchLatest();
      if (!data) return;
      applyResult(data);
      if (data.status === 'COMPLETED' || data.status === 'FAILED') return false;
    },
    POLL_INTERVAL,
    isRunning,
  );

  useEffect(() => {
    fetchLatest().then(applyResult);
  }, [fetchLatest, applyResult]);

  const handleReanalyze = async () => {
    setTriggering(true);
    setErrorMessage(null);
    try {
      if (type === 'bid') {
        await bidAnalysisApi.trigger(opportunityId);
      } else {
        await analysisApi.trigger(opportunityId);
      }
      // Move to PENDING so usePolling (keyed on isRunning) takes over.
      setStatus('PENDING');
      setScore(null);
    } catch {
      setErrorMessage('Failed to trigger analysis');
    } finally {
      setTriggering(false);
    }
  };

  const label = type === 'bid' ? 'AI Bid Analysis Score' : 'AI Proposal Analysis Score';
  const isCompleted = status === 'COMPLETED';
  const isFailed = status === 'FAILED';

  // No analysis exists yet
  if (!status) {
    return (
      <Card
        size="small"
        style={{
          marginTop: 12,
          marginBottom: 12,
          borderRadius: 8,
          borderLeft: '4px solid var(--color-border)',
          background: 'var(--color-bg-muted)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <ExperimentOutlined style={{ fontSize: 20, color: 'var(--color-text-tertiary)' }} />
          <div>
            <Text strong style={{ fontSize: 14, display: 'block' }}>{label}</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              AI analysis pending… will run automatically.
            </Text>
          </div>
        </div>
      </Card>
    );
  }

  // Running / Pending
  if (isRunning) {
    return (
      <Card
        size="small"
        style={{
          marginTop: 12,
          marginBottom: 12,
          borderRadius: 8,
          borderLeft: '4px solid var(--color-primary)',
          background: 'var(--color-bg-container)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Spin size="default" />
          <div>
            <Text strong style={{ fontSize: 14, display: 'block' }}>{label}</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              AI analysis in progress…
            </Text>
          </div>
        </div>
      </Card>
    );
  }

  // Failed
  if (isFailed) {
    return (
      <Card
        size="small"
        style={{
          marginTop: 12,
          marginBottom: 12,
          borderRadius: 8,
          borderLeft: '4px solid var(--color-error)',
          background: 'var(--color-error-light)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <ExperimentOutlined style={{ fontSize: 20, color: 'var(--color-error)' }} />
          <div style={{ flex: 1 }}>
            <Text strong style={{ fontSize: 14, display: 'block' }}>{label}</Text>
            <Text type="danger" style={{ fontSize: 12 }}>
              {errorMessage || 'Analysis failed'}
            </Text>
          </div>
          <Button
            size="small"
            icon={<ReloadOutlined />}
            onClick={handleReanalyze}
            loading={triggering}
          >
            Re-analyze
          </Button>
        </div>
      </Card>
    );
  }

  // Completed
  if (isCompleted && score != null) {
    const passed = score >= SCORE_THRESHOLD;
    return (
      <Card
        size="small"
        style={{
          marginTop: 12,
          marginBottom: 12,
          borderRadius: 8,
          borderLeft: `4px solid ${passed ? 'var(--color-success)' : 'var(--color-error)'}`,
          background: passed ? 'var(--color-success-light)' : 'var(--color-error-light)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <Progress
            type="circle"
            percent={score}
            size={56}
            strokeColor={getScoreColor(score)}
            format={(pct) => <span style={{ fontSize: 16, fontWeight: 700 }}>{pct}%</span>}
          />
          <div style={{ flex: 1 }}>
            <Text strong style={{ fontSize: 14, display: 'block' }}>{label}</Text>
            {passed ? (
              <Text type="success" style={{ fontSize: 12 }}>
                Score meets {SCORE_THRESHOLD}% threshold — ready
              </Text>
            ) : (
              <Text type="danger" style={{ fontSize: 12 }}>
                Re-review recommended — score is below {SCORE_THRESHOLD}%
              </Text>
            )}
          </div>
          {!passed && (
            <Button
              size="small"
              icon={<ReloadOutlined />}
              onClick={handleReanalyze}
              loading={triggering}
            >
              Re-analyze
            </Button>
          )}
        </div>
      </Card>
    );
  }

  return null;
};

export default AIScoreCard;
